import {
  BadgeCategoriaConcreto,
  BadgeCumplimiento,
  FechaCR,
  ModalImportarExcelLab,
} from '@/components/dominio';
import { ModalCrearMuestra } from '@/components/dominio/ModalCrearMuestra';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useActividades, useMuestras } from '@/hooks/useLaboratorio';
import { puedeEscribirLab } from '@/lib/cliente';
import { esAdmin } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import { PROYECTOS_LAB } from '@adelante/shared';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  FileUp,
  Plus,
  Search,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * `/lab/muestras` — listado de muestras de laboratorio con filtros y paginación.
 *
 * Filtros (persistidos en URL):
 *   - obra_works_no (input simple, validamos solo formato)
 *   - id_actividad (select del catálogo)
 *   - fc_objetivo (input numérico)
 *   - desde/hasta (rango de fecha_colado)
 *   - q (búsqueda libre en id_casa / actividad / proveedor / obra)
 *
 * Click en fila → /lab/muestras/:id (preserva filtros en state.from).
 */

const POR_PAGINA = 25;
const SKELETON_FILAS = ['s-a', 's-b', 's-c', 's-d', 's-e'] as const;
const SKELETON_COLS = ['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9', 'c10'] as const;

export function MuestrasListaPantalla() {
  const navegar = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper para escribir múltiples params en una sola llamada (evita race
  // con dos setSearchParams seguidos — el segundo pisaba al primero).
  const setParams = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const obraWorksNo = searchParams.get('obra_works_no') ?? '';
  const proyectoRaw = searchParams.get('proyecto') ?? 'todos';
  const proyecto = (PROYECTOS_LAB as readonly string[]).includes(proyectoRaw)
    ? proyectoRaw
    : 'todos';
  const idActividadRaw = searchParams.get('id_actividad');
  const idActividad =
    idActividadRaw !== null && /^\d+$/.test(idActividadRaw) ? Number(idActividadRaw) : null;
  const fcObjetivoRaw = searchParams.get('fc_objetivo');
  const fcObjetivo =
    fcObjetivoRaw !== null && /^\d+$/.test(fcObjetivoRaw) ? Number(fcObjetivoRaw) : null;
  const desde = searchParams.get('desde') ?? '';
  const hasta = searchParams.get('hasta') ?? '';
  const q = searchParams.get('q') ?? '';
  const cumplimientoRaw = searchParams.get('cumplimiento') ?? 'todos';
  const cumplimiento: 'todos' | 'cumple' | 'marginal' | 'incumple' | 'sin_dato' = [
    'cumple',
    'marginal',
    'incumple',
    'sin_dato',
  ].includes(cumplimientoRaw)
    ? (cumplimientoRaw as 'cumple' | 'marginal' | 'incumple' | 'sin_dato')
    : 'todos';
  const paginaRaw = searchParams.get('pagina');
  const pagina = paginaRaw !== null && /^\d+$/.test(paginaRaw) ? Math.max(1, Number(paginaRaw)) : 1;

  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalImport, setModalImport] = useState(false);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const mostrarImport = esAdmin();

  const cantidadFiltrosExtra = [
    proyecto !== 'todos',
    obraWorksNo.trim() !== '',
    idActividad !== null,
    fcObjetivo !== null,
    cumplimiento !== 'todos',
    desde !== '',
    hasta !== '',
  ].filter(Boolean).length;

  const actividades = useActividades(false);
  const filtros = useMemo(
    () => ({
      obra_works_no: obraWorksNo.trim() || undefined,
      proyecto: proyecto === 'todos' ? undefined : proyecto,
      id_actividad: idActividad ?? undefined,
      fc_objetivo: fcObjetivo ?? undefined,
      desde: desde || undefined,
      hasta: hasta || undefined,
      q: q.trim() || undefined,
      cumplimiento: cumplimiento === 'todos' ? undefined : cumplimiento,
      pagina,
      por_pagina: POR_PAGINA,
    }),
    [obraWorksNo, proyecto, idActividad, fcObjetivo, desde, hasta, q, cumplimiento, pagina],
  );

  const consulta = useMuestras(filtros);
  const data = consulta.data?.data;
  const muestras = data?.muestras ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  const puedeEscribir = puedeEscribirLab();

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Laboratorio — Muestras</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Control de resistencia a la compresión del concreto. Cada muestra puede tener varios
            ensayos a distintas edades (7, 14, 28 días, etc.).
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:flex-nowrap">
          {mostrarImport && (
            <Button
              variant="outline"
              onClick={() => setModalImport(true)}
              className="gap-1.5"
              title="Importar el Excel histórico de laboratorio"
            >
              <FileUp className="h-4 w-4" /> Importar Excel
            </Button>
          )}
          {puedeEscribir && (
            <Button onClick={() => setModalNuevo(true)} className="flex-1 gap-1.5 sm:flex-none">
              <Plus className="h-4 w-4" /> Nueva muestra
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="grid w-full gap-1.5 md:order-last md:flex-1 md:min-w-[200px]">
            <Label htmlFor="f-q">Buscar</Label>
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="f-q"
                value={q}
                onChange={(e) => setParams({ q: e.target.value || null, pagina: null })}
                placeholder="Casa, actividad, proveedor, obra..."
                className="pl-8"
              />
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setFiltrosAbiertos((v) => !v)}
            className="w-full justify-between gap-1.5 md:hidden"
          >
            <span>
              Más filtros
              {cantidadFiltrosExtra > 0 && (
                <span className="ml-1.5 rounded-full bg-zinc-200 px-1.5 py-0.5 text-xs font-medium">
                  {cantidadFiltrosExtra}
                </span>
              )}
            </span>
            {filtrosAbiertos ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>

          <div
            className={cn(
              'w-full flex-wrap items-end gap-4 md:flex',
              filtrosAbiertos ? 'flex' : 'hidden',
            )}
          >
            <div className="grid w-full gap-1.5 sm:w-auto">
              <Label htmlFor="f-proyecto">Proyecto</Label>
              <Select
                value={proyecto}
                onValueChange={(v) =>
                  setParams({ proyecto: v === 'todos' ? null : v, pagina: null })
                }
              >
                <SelectTrigger id="f-proyecto" className="w-full sm:w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {PROYECTOS_LAB.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid w-full gap-1.5 sm:w-auto">
              <Label htmlFor="f-obra">Obra (works_no)</Label>
              <Input
                id="f-obra"
                value={obraWorksNo}
                onChange={(e) => setParams({ obra_works_no: e.target.value || null, pagina: null })}
                placeholder="VB, 6.24, Casa…"
                className="w-full font-mono sm:w-[160px]"
              />
            </div>

            <div className="grid w-full gap-1.5 sm:w-auto">
              <Label htmlFor="f-actividad">Actividad</Label>
              <Select
                value={idActividad === null ? 'todas' : String(idActividad)}
                onValueChange={(v) =>
                  setParams({ id_actividad: v === 'todas' ? null : v, pagina: null })
                }
              >
                <SelectTrigger id="f-actividad" className="w-full sm:w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  {(actividades.data?.data?.actividades ?? []).map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid w-full gap-1.5 sm:w-auto">
              <Label htmlFor="f-fc">F'C objetivo</Label>
              <Input
                id="f-fc"
                type="number"
                value={fcObjetivo ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setParams({ fc_objetivo: v && /^\d+$/.test(v) ? v : null, pagina: null });
                }}
                placeholder="210"
                className="w-full sm:w-[120px]"
                min={1}
              />
            </div>

            <div className="grid w-full gap-1.5 sm:w-auto">
              <Label htmlFor="f-cumplimiento">Cumplimiento</Label>
              <Select
                value={cumplimiento}
                onValueChange={(v) =>
                  setParams({ cumplimiento: v === 'todos' ? null : v, pagina: null })
                }
              >
                <SelectTrigger id="f-cumplimiento" className="w-full sm:w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="incumple">No cumple</SelectItem>
                  <SelectItem value="marginal">Marginal</SelectItem>
                  <SelectItem value="cumple">Cumple</SelectItem>
                  <SelectItem value="sin_dato">Sin datos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid w-full gap-1.5 sm:w-auto">
              <Label htmlFor="f-desde">Desde</Label>
              <Input
                id="f-desde"
                type="date"
                value={desde}
                onChange={(e) => setParams({ desde: e.target.value || null, pagina: null })}
                className="w-full sm:w-[160px]"
              />
            </div>

            <div className="grid w-full gap-1.5 sm:w-auto">
              <Label htmlFor="f-hasta">Hasta</Label>
              <Input
                id="f-hasta"
                type="date"
                value={hasta}
                onChange={(e) => setParams({ hasta: e.target.value || null, pagina: null })}
                className="w-full sm:w-[160px]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {/* Lista de tarjetas (móvil) */}
          <div className="space-y-2 p-2 md:hidden">
            {consulta.isLoading &&
              ['s-a', 's-b', 's-c'].map((k) => (
                <div key={k} className="h-24 animate-pulse rounded-md border bg-muted" />
              ))}
            {!consulta.isLoading && muestras.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No hay muestras que coincidan con los filtros.
              </div>
            )}
            {!consulta.isLoading &&
              muestras.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    navegar(`/lab/muestras/${m.id}`, {
                      state: { from: `${location.pathname}${location.search}` },
                    });
                  }}
                  className="flex w-full flex-col gap-1 rounded-md border bg-white p-3 text-left transition hover:bg-zinc-50 active:bg-zinc-100"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono text-base font-semibold leading-tight">
                      {m.obra_works_no || (
                        <span className="font-sans italic text-muted-foreground">Sin obra</span>
                      )}
                    </span>
                    <BadgeCumplimiento evaluacion={m.evaluacion_cumplimiento} />
                  </div>
                  {m.id_casa && (
                    <div className="truncate text-base text-foreground/80">{m.id_casa}</div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    <FechaCR fecha={m.fecha_colado} formato="corto" /> · {m.actividad_nombre} ·{' '}
                    <span className="font-mono">#{m.numero_muestra}</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="font-mono font-medium">F'C {m.fc_objetivo}</span>
                    <span className="text-muted-foreground">·</span>
                    <span>
                      {m.cantidad_ensayos} ensayo{m.cantidad_ensayos === 1 ? '' : 's'}
                    </span>
                    {m.edades_completadas.length > 0 && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        {m.edades_completadas.map((d) => (
                          <span
                            key={d}
                            className={cn(
                              'inline-flex rounded-full border px-1.5 py-0.5 font-mono',
                              d === 28
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                                : 'border-zinc-200 bg-zinc-50 text-zinc-700',
                            )}
                          >
                            {d}d
                          </span>
                        ))}
                      </>
                    )}
                  </div>
                </button>
              ))}
          </div>

          {/* Tabla (desktop) */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">#</TableHead>
                  <TableHead>Fecha colado</TableHead>
                  <TableHead>Planta</TableHead>
                  <TableHead>Obra</TableHead>
                  <TableHead>Casa / ubic.</TableHead>
                  <TableHead>Actividad</TableHead>
                  <TableHead>Tipo de concreto</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead className="text-right">F'C</TableHead>
                  <TableHead>Cumplimiento</TableHead>
                  <TableHead className="text-right">Ensayos</TableHead>
                  <TableHead>Edades probadas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consulta.isLoading &&
                  SKELETON_FILAS.map((idFila) => (
                    <TableRow key={idFila}>
                      {SKELETON_COLS.map((idCol) => (
                        <TableCell key={`${idFila}-${idCol}`}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {!consulta.isLoading && muestras.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      No hay muestras que coincidan con los filtros.
                    </TableCell>
                  </TableRow>
                )}
                {!consulta.isLoading &&
                  muestras.map((m) => (
                    <TableRow
                      key={m.id}
                      onClick={() => {
                        navegar(`/lab/muestras/${m.id}`, {
                          state: { from: `${location.pathname}${location.search}` },
                        });
                      }}
                      className="cursor-pointer hover:bg-zinc-50"
                    >
                      <TableCell className="font-mono font-medium">{m.numero_muestra}</TableCell>
                      <TableCell className="font-mono text-sm">
                        <FechaCR fecha={m.fecha_colado} formato="corto" />
                      </TableCell>
                      <TableCell className="text-sm">
                        {m.planta_nombre || <span className="italic text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {m.obra_works_no ? (
                          <div title={m.obra_display_name ?? m.obra_works_no}>
                            <div className="font-mono text-xs">{m.obra_works_no}</div>
                            {m.obra_display_name && (
                              <div className="truncate text-xs text-muted-foreground max-w-[180px]">
                                {m.obra_display_name}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="italic text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {m.id_casa || <span className="italic text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-sm">{m.actividad_nombre}</TableCell>
                      <TableCell className="text-sm">{m.tipo_concreto_display}</TableCell>
                      <TableCell>
                        <BadgeCategoriaConcreto categoria={m.categoria_concreto} />
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {m.fc_objetivo}
                      </TableCell>
                      <TableCell>
                        <BadgeCumplimiento evaluacion={m.evaluacion_cumplimiento} />
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {m.cantidad_ensayos}
                      </TableCell>
                      <TableCell className="text-sm">
                        {m.edades_completadas.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {m.edades_completadas.map((d) => (
                              <span
                                key={d}
                                className={cn(
                                  'inline-flex rounded-full border px-1.5 py-0.5 text-xs font-mono',
                                  d === 28
                                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                                    : 'border-zinc-200 bg-zinc-50 text-zinc-700',
                                )}
                              >
                                {d}d
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="italic text-muted-foreground text-xs">sin probar</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {!consulta.isLoading && total > 0 && (
            <div className="flex flex-col gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                {muestras.length === 0
                  ? '0 resultados'
                  : `Mostrando ${(pagina - 1) * POR_PAGINA + 1}–${
                      (pagina - 1) * POR_PAGINA + muestras.length
                    } de ${total}`}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina <= 1}
                  onClick={() =>
                    setParams({
                      pagina: pagina - 1 <= 1 ? null : String(pagina - 1),
                    })
                  }
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <span className="text-sm tabular-nums">
                  {pagina} / {totalPaginas}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina >= totalPaginas}
                  onClick={() => setParams({ pagina: String(pagina + 1) })}
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {consulta.error && (
        <p className="text-sm text-destructive">
          Error al cargar muestras: {consulta.error.message}
        </p>
      )}

      <ModalImportarExcelLab abierto={modalImport} cerrar={() => setModalImport(false)} />

      <ModalCrearMuestra
        abierto={modalNuevo}
        cerrar={() => setModalNuevo(false)}
        onCreado={(idMuestra) => {
          // Navegamos al detalle de la recién creada para cargar mediciones.
          // Como el backend pre-crea los ensayos 7/14/28, el detalle ya
          // muestra las tarjetas listas para recibir MPa.
          navegar(`/lab/muestras/${idMuestra}`, {
            state: { from: `${location.pathname}${location.search}` },
          });
        }}
      />
    </div>
  );
}
